export const getFalse = () => false;

export const getTrue = () => true;

/**
 * alphabetize object keys to ensure consistent serialization
 */
export function normalize(value: any) {
  return Array.isArray(value) || typeof value !== "object"
    ? value
    : Object.keys(value)
        .filter((key) => value[key] !== undefined)
        .sort()
        .reduce(
          (obj, key) => {
            obj[key] = normalize(value[key]);
            return obj;
          },
          {} as Partial<Record<string, string>>
        );
}

export function serialize(value: any) {
  return JSON.stringify(normalize(value));
}

/**
 * This generic ExplicitType is used to identify object types by a static string field
 * The first argument is the value in that field
 *
 * Example:
 *    const simplePredictor: ExplicitType<"Simple"> = {
 *      type: "Simple",
 *    };
 *
 * The optional second argument is the key used to store the static value
 * It defaults to "type" and is listed second because it is optional
 *
 * Example:
 *    const designSpace: ExplicitType<"DESIGN_SPACE", "module_type"> = {
 *      module_type: "DESIGN_SPACE",
 *    };
 *
 * This generic ExplicitType is used to define simpler typeguards
 */
export type ExplicitType<Value extends string, Key extends string = "type"> = {
  [k in Key]: Value;
};

/**
 * This helper allows you to map/select union subtypes by their explicit type value
 *
 * Example:
 *    type One = ExplicitType<"one">;
 *    type Two = ExplicitType<"two">;
 *
 *    type Number = One | Two;
 *    type NumberMap = ExplicitTypeMap<Number>;
 *
 *    // The above line is equivalent to:
 *    interface NumberMap {
 *      one: One;
 *      two: Two;
 *    }
 */
export type ExplicitTypeMap<
  Type extends ExplicitType<string, Key>,
  Key extends string = "type",
> = {
  [Value in Type[Key]]: Type extends Record<Key, Value> ? Type : never;
};
export type ExplicitArrayMap<
  Type extends ExplicitType<string, Key>,
  Key extends string = "type",
> = {
  [Value in Type[Key]]: Type extends Record<Key, Value> ? Type[] : never;
};

/**
 * This isFromKey is a curried function that is used to generate typeguards
 *
 * It should first be called with the key used for lookup on the object
 * The resulting function should then be called with the value to match
 * This final function is the typeguard to be used on your objects
 *
 * Example:
 *    const isOfType = isFromKey("type");
 *    const isCsvData = isOfType("csv_data_source");
 *    assert(isCsvData({ type: "csv_data_source" }));
 *
 * @param key {string}
 */
export const isFromKey =
  <Key extends string>(key: Key) =>
  <
    SpecificType extends GenericType,
    GenericType extends ExplicitType<string, Key> = ExplicitType<string, Key>,
  >(
    // typed as a tuple instead of a simple array to ensure at least one val is provided
    ...vals: [SpecificType[Key], ...SpecificType[Key][]]
  ) =>
  (obj?: GenericType): obj is SpecificType =>
    (vals as any[]).includes(obj?.[key]);

export const isOfType = isFromKey("type");
export const isOfModuleType = isFromKey("module_type");
